import React, { useState, useEffect, useRef } from 'react';
import { api, API, STANDARD_STAGES, EMERGENCY_STAGES, STAGE_LABELS, STAGE_COLORS, fmtMoney, timeSince } from './constants';
import { X, Phone, Mail, MapPin, Clock, FileText, Upload, Trophy, Ban, Archive, Save, Siren, AlertTriangle, Globe, User as UserIcon, MessageSquare, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';

export default function LeadDetailModal({ leadId, users, onClose, onChanged }) {
  const [lead, setLead] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [edit, setEdit] = useState({});
  const fileRef = useRef(null);

  const load = async () => {
    const res = await api.get(`/leads/${leadId}`);
    if (res.ok) {
      const data = await res.json();
      setLead(data);
      setEdit({
        name: data.name || '', phone: data.phone || '', email: data.email || '',
        address: data.address || '', city: data.city || '', zip: data.zip || '',
        estimated_value: data.estimated_value || '', follow_up_date: data.follow_up_date || '',
        assigned_to: data.assigned_to || '', description: data.description || '',
      });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const isEmergency = lead?.lead_type === 'emergency';
  const stages = isEmergency ? EMERGENCY_STAGES : STANDARD_STAGES;

  const patch = async (body, msg) => {
    setSaving(true);
    const res = await api.patch(`/leads/${leadId}`, body);
    setSaving(false);
    if (res.ok) { toast.success(msg || 'Updated'); await load(); onChanged?.(); }
    else toast.error('Update failed');
  };

  const saveFields = () => patch(edit, 'Lead saved');

  const addNote = async () => {
    if (!note.trim()) return;
    const res = await api.post(`/leads/${leadId}/notes`, { note });
    if (res.ok) { setNote(''); toast.success('Note added'); await load(); }
    else toast.error('Failed to add note');
  };

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    const res = await api.upload(`/leads/${leadId}/files`, fd);
    if (res.ok) { toast.success('Files uploaded'); await load(); onChanged?.(); }
    else toast.error('Upload failed');
  };

  if (!lead) return null;

  const inputCls = 'w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-white text-sm focus:border-orange-500/50 focus:outline-none';
  const labelCls = 'text-slate-500 text-xs mb-1 block';

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/70 overflow-y-auto p-4" data-testid="lead-detail-modal" onClick={onClose}>
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-4xl my-8" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isEmergency ? 'border-red-500/30 bg-red-950/30' : 'border-slate-700'} rounded-t-xl`}>
          <div className="flex items-center gap-3">
            {isEmergency && <Siren className="w-5 h-5 text-red-400" />}
            <div>
              <h2 className="text-xl font-bold text-white">{lead.name}</h2>
              <p className="text-slate-400 text-xs">
                {isEmergency ? 'Emergency' : 'Standard'} · {lead.source} · {new Date(lead.created_at).toLocaleString()}
              </p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${STAGE_COLORS[lead.status] || ''}`}>{STAGE_LABELS[lead.status] || lead.status}</span>
          </div>
          <button onClick={onClose} data-testid="lead-detail-close" className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {isEmergency && (
          <div className="mx-6 mt-4 flex flex-wrap items-center gap-4 bg-red-950/30 border border-red-500/30 rounded px-4 py-2.5">
            <span className="flex items-center gap-1.5 text-red-300 text-sm"><AlertTriangle className="w-4 h-4" />{lead.emergency_category}</span>
            <span className="flex items-center gap-1.5 text-amber-300 text-xs"><Clock className="w-3.5 h-3.5" />Elapsed: {timeSince(lead.created_at)}</span>
            {lead.active_damage && <span className="text-xs text-red-200">Active damage: {lead.active_damage}</span>}
            {lead.issue_started && <span className="text-xs text-slate-400">Started: {lead.issue_started}</span>}
          </div>
        )}

        {/* Pipeline */}
        <div className="px-6 pt-4">
          <div className="text-slate-500 text-xs mb-2">Pipeline Stage</div>
          <div className="flex flex-wrap gap-2">
            {stages.map(s => (
              <button key={s} data-testid={`stage-btn-${s}`} onClick={() => patch({ status: s }, `Moved to ${STAGE_LABELS[s]}`)}
                className={`text-xs px-3 py-1.5 rounded border transition-all ${lead.status === s ? (STAGE_COLORS[s] || 'bg-orange-500 text-white') : 'border-slate-600 text-slate-400 hover:border-slate-500'}`}>
                {STAGE_LABELS[s]}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={() => patch({ status: isEmergency ? 'completed' : 'won' }, 'Marked won/complete')} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-green-600 hover:bg-green-700 text-white"><Trophy className="w-3.5 h-3.5" />{isEmergency ? 'Complete' : 'Mark Won'}</button>
            <button onClick={() => patch({ status: isEmergency ? 'closed' : 'lost' }, 'Marked lost/closed')} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white"><Ban className="w-3.5 h-3.5" />{isEmergency ? 'Close' : 'Mark Lost'}</button>
            <button onClick={() => { patch({ archived: true }, 'Lead archived'); onClose(); }} data-testid="archive-lead-btn" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-200"><Archive className="w-3.5 h-3.5" />Archive</button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          {/* Editable customer + property */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2"><UserIcon className="w-4 h-4 text-orange-400" />Customer & Property</h3>
            <div><label className={labelCls}>Name</label><input data-testid="edit-name" className={inputCls} value={edit.name} onChange={e => setEdit(p => ({ ...p, name: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Phone</label><input className={inputCls} value={edit.phone} onChange={e => setEdit(p => ({ ...p, phone: e.target.value }))} /></div>
              <div><label className={labelCls}>Email</label><input className={inputCls} value={edit.email} onChange={e => setEdit(p => ({ ...p, email: e.target.value }))} /></div>
            </div>
            <div><label className={labelCls}>Address</label><input className={inputCls} value={edit.address} onChange={e => setEdit(p => ({ ...p, address: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>City</label><input className={inputCls} value={edit.city} onChange={e => setEdit(p => ({ ...p, city: e.target.value }))} /></div>
              <div><label className={labelCls}>ZIP</label><input className={inputCls} value={edit.zip} onChange={e => setEdit(p => ({ ...p, zip: e.target.value }))} /></div>
            </div>
            <div><label className={labelCls}>Description</label><textarea rows={3} className={`${inputCls} resize-none`} value={edit.description} onChange={e => setEdit(p => ({ ...p, description: e.target.value }))} /></div>
          </div>

          {/* Management */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2"><CalendarClock className="w-4 h-4 text-teal-400" />Management</h3>
            <div><label className={labelCls}>Assigned Team Member</label>
              <select data-testid="assign-select" className={inputCls} value={edit.assigned_to} onChange={e => setEdit(p => ({ ...p, assigned_to: e.target.value }))}>
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.email}>{u.name} ({u.role})</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Estimated Value</label><input data-testid="edit-value" className={inputCls} placeholder="$" value={edit.estimated_value} onChange={e => setEdit(p => ({ ...p, estimated_value: e.target.value }))} /></div>
              <div><label className={labelCls}>Follow-Up Date</label><input type="date" className={inputCls} value={edit.follow_up_date} onChange={e => setEdit(p => ({ ...p, follow_up_date: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-slate-400 bg-slate-900/60 rounded p-3">
              <div className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-orange-400" />Language: <span className="text-white uppercase">{lead.language || 'en'}</span></div>
              <div className="flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5 text-orange-400" />Contact: <span className="text-white">{lead.contact_method}</span></div>
              <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-orange-400" />Budget: <span className="text-white">{lead.budget || '—'}</span></div>
              <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-orange-400" />Start: <span className="text-white">{lead.timeline || '—'}</span></div>
            </div>
            {(lead.services?.length > 0) && (
              <div><label className={labelCls}>Services Requested</label>
                <div className="flex flex-wrap gap-1.5">{lead.services.map((s, i) => <span key={i} className="text-xs bg-slate-700 text-slate-200 px-2 py-1 rounded">{s}</span>)}</div>
              </div>
            )}
            <button onClick={saveFields} disabled={saving} data-testid="save-lead-btn" className="flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold py-2.5 rounded transition-colors disabled:opacity-50"><Save className="w-4 h-4" />Save Changes</button>
          </div>
        </div>

        {/* Files */}
        <div className="px-6 pb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2"><FileText className="w-4 h-4 text-teal-400" />Files & Photos ({lead.files?.length || 0})</h3>
            <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/jpg,image/heic,application/pdf" onChange={uploadFiles} className="hidden" />
            <button onClick={() => fileRef.current?.click()} data-testid="upload-file-btn" className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white border border-slate-600 rounded px-3 py-1.5"><Upload className="w-3.5 h-3.5" />Upload</button>
          </div>
          {lead.files?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {lead.files.map(f => (
                <a key={f.id} href={`${API}/api/files/${f.id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-slate-900 rounded px-3 py-2 text-xs text-slate-300 hover:text-white border border-slate-700">
                  <FileText className="w-4 h-4 text-orange-400" />{f.filename}
                </a>
              ))}
            </div>
          ) : <p className="text-slate-500 text-xs">No files uploaded</p>}
        </div>

        {/* Notes + Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-6 pb-6">
          <div>
            <h3 className="text-sm font-semibold text-slate-300 mb-2">Add Internal Note</h3>
            <textarea rows={3} data-testid="note-input" className={`${inputCls} resize-none`} value={note} onChange={e => setNote(e.target.value)} placeholder="Type a note..." />
            <button onClick={addNote} data-testid="add-note-btn" className="mt-2 bg-slate-700 hover:bg-slate-600 text-white text-sm px-4 py-2 rounded">Add Note</button>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-300 mb-2">Activity History</h3>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-2" data-testid="activity-timeline">
              {lead.activities?.length ? lead.activities.map(a => (
                <div key={a.id} className="text-xs border-l-2 border-slate-600 pl-3 py-1">
                  <div className="text-slate-200">{a.message}</div>
                  <div className="text-slate-500">{a.actor} · {new Date(a.created_at).toLocaleString()}</div>
                </div>
              )) : <p className="text-slate-500 text-xs">No activity yet</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
